// --------------------------------------------------------------
// Created On: 2024-03-26
// Author: Dimitra Weinstein
//
// Last Modified: 2025-06-09
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import getApiError from "../../utilities/api/getApiError";
import { API } from "../../constants/miscellaneous";
import Spinner from "../../components/Spinner/Spinner";
import PartModal from "../../components/PartModal/PartModal";
import PartNotSellablePage from "../PartNotSellablePage/PartNotSellablePage";
import BomDetails from "./BomDetails/BomDetails";
import BomSvgExplorer from "./BomSvgExplorer/BomSvgExplorer";
import Toast from "../../components/Toast/Toast";
import PartsList from "./PartsList/PartsList";
import useReduxSelector from "src/hooks/useReduxSelector";
import deepCopy from "../../utilities/deepCopy";
import stringIsInteger from "../../utilities/stringIsInteger";
import stringIsValidNumber from "../../utilities/stringIsValidNumber";
import styles from "./BomLookUpPage.module.scss";

// Page where a user can view details of a BOM and its child parts, customize and place items in the shopping cart.
export default function BomLookUpPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [addToCartMessage, setAddToCartMessage] = useState<string>("");
  const [bom, setBom] = useState<PartFromApi | null>(null);
  const [bomNumber, setBomNumber] = useState<string>("");
  const [pumpModel, setPumpModel] = useState<string>("");
  const [trim, setTrim] = useState<number | null>(null);
  const [serialNumber, setSerialNumber] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState<string>("");
  const [pumpCurves, setPumpCurves] = useState<PumpCurve[]>([]);
  const [bomPath, setBomPath] = useState<string[]>([]);
  const [svgFound, setSvgFound] = useState<boolean>(false);
  const [hoveredPartNumber, setHoveredPartNumber] = useState<string | null>(null);
  const [companyDiscount, setCompanyDiscount] = useState<number | null>(null);
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
  const [showAddAllPartsToCartModal, setShowAddAllPartsToCartModal] = useState<boolean>(false);
  const [defaultPaintColorId, setDefaultPaintColorId] = useState<number | null>(null);
  const [currentParts, setCurrentParts] = useState<Part[]>([]);
  const [forceNestedBom, setForceNestedBom] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const bomIsFlat = (bom === null || bomPath === null || !svgFound) && !forceNestedBom;
  const { type, identifier } = useParams();
  const lookupPath = `lookup/${type}/${identifier}`;
  const flatParts = useMemo(() => getFlatParts(bom), [JSON.stringify(bom)]);
  const selectedPart = useMemo(
    () => getPart(selectedPartId, currentParts),
    [selectedPartId, JSON.stringify(currentParts)]
  );

  // Get data from api.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/${lookupPath}`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setFailedToLoad(false);
        setBomNumber(responseBody.bomNumber);
        setPumpModel(responseBody.pumpModel);
        setTrim(responseBody.trim);
        setSerialNumber(responseBody.serialNumber);
        setManualUrl(responseBody.manualUrl);
        setPumpCurves(responseBody.pumpCurves);
        setForceNestedBom(responseBody.forceNestedBom);
        setBom(responseBody.bom);
        setBomPath([responseBody.bom.partNumber]);
        setSvgFound(await findSvg(responseBody.bom.partNumber));
        setCompanyDiscount(responseBody.companyDiscount);
        setErrorMessage("");
      } else {
        setFailedToLoad(true);
        setErrorMessage(await getApiError(response, "Couldn't retrieve part information."));
      }
      setLoading(false);
    },
    [type, identifier]
  );

  // Get company default paint color.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/company/${currentUser.companyId}/preference`,
      method: "GET",
    },
    async (response: Response, responseBody: CompanyPreferenceResponseBody) => {
      if (response.ok && responseBody && responseBody.defaultPaintColor !== null) {
        setErrorMessage("");
        setDefaultPaintColorId(responseBody.defaultPaintColor.paintColorId);
      } else if (!response.ok) {
        setErrorMessage(await getApiError(response, "Unable to retrieve company preference setting."));
      }
    },
    []
  );

  // Whenever the BOM or paths are recalculated, clear all modifications to existing parts and
  // recalculate what the currently shown parts are.
  useEffect(() => {
    if (bomIsFlat) {
      setCurrentParts(flatParts);
    } else {
      setCurrentParts(findPartsByPath(bomPath, bom));
    }
  }, [JSON.stringify(bom), JSON.stringify(bomPath), svgFound, bomIsFlat, JSON.stringify(flatParts)]);

  // Function that get a flat list of all child parts from a parent BOM part.
  function getFlatParts(part: PartFromApi | null): Part[] {
    let flatParts: Part[] = [];
    if (part !== null) {
      part.parts.forEach((part) => {
        if (part.isSellable) {
          flatParts.push(addExtraFieldsToPart(part));
        }
        flatParts = [...flatParts, ...getFlatParts(part)];
      });
    }
    return flatParts;
  }

  // Convert part from API to a part with extra initialized fields used for tracking extra customizable part details.
  function addExtraFieldsToPart(part: PartFromApi): Part {
    return {
      partId: part.partId,
      partNumber: part.partNumber,
      description: part.description,
      unitId: part.unitId,
      units: part.units,
      price: part.price,
      buildQuantity: part.buildQuantity === null ? "1" : part.buildQuantity,
      paintColorId: part.isPaintable ? defaultPaintColorId : null,
      orderQuantity: "1",
      weight: part.weight,
      isPaintable: part.isPaintable,
      isSellable: part.isSellable,
      isImpeller: part.isImpeller,
      impellerTrim: "0.0",
      impellerAngle: "0",
      hasOtherMaterial: part.hasOtherMaterial,
      parts: part.parts,
    };
  }

  // Given a sequence of parts with the BOM being the root find the children of the final part in the path.
  function findPartsByPath(bomPath: string[], bom: PartFromApi | null): Part[] {
    if (bom === null) {
      return [];
    }
    if (bomPath.length > 1) {
      const shortBomPath = bomPath.slice(1);
      let currentPart = bom;
      shortBomPath.forEach((bomPathElement) => {
        currentPart.parts.forEach((part) => {
          if (part.partNumber === bomPathElement) {
            currentPart = part;
          }
        });
      });
      return currentPart.parts.map((part) => addExtraFieldsToPart(part));
    } else {
      return bom.parts.map((part) => addExtraFieldsToPart(part));
    }
  }

  // Check if an svg exists for given part number.
  async function findSvg(partNumber: string) {
    const result = await fetch(`/svgs/${partNumber}.svg`, { method: "HEAD" });
    return result.ok;
  }

  // Returns a part given the part ID.
  function getPart(partId: number | null, partsList: Part[]): Part | null {
    if (partId !== null) {
      const part = partsList.find((part) => part.partId === partId);
      if (part !== undefined) {
        return part;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  // When closing the part modal, sets specific forms back to default values so they don't persist between selections.
  function handleCloseModal() {
    const currentPartsDeepCopy = deepCopy(currentParts);
    const partIndex = getPartIndex(selectedPartId, currentPartsDeepCopy);
    if (partIndex !== -1) {
      currentPartsDeepCopy[partIndex].orderQuantity = "1";
      currentPartsDeepCopy[partIndex].impellerAngle = "0";
      currentPartsDeepCopy[partIndex].impellerTrim = "0.0";
      if (currentPartsDeepCopy[partIndex].isPaintable === true) {
        currentPartsDeepCopy[partIndex].paintColorId = defaultPaintColorId;
      } else {
        currentPartsDeepCopy[partIndex].paintColorId = null;
      }
    }
    setCurrentParts(currentPartsDeepCopy);
    setSelectedPartId(null);
    setShowAddAllPartsToCartModal(false);
  }

  // Returns a part given the part ID.
  function getPartIndex(partId: number | null, currentPartsDeepCopy: Part[]): number {
    return currentPartsDeepCopy.findIndex((part) => part.partId === partId);
  }

  // Validates that only numbers are able to be input in quantity field prior to updating.
  function validateQuantity(quantity: string) {
    if ((parseInt(quantity, 10) > 0 || quantity === "") && quantity.indexOf(".") === -1 && stringIsInteger(quantity)) {
      return true;
    }
  }

  // Updates the quantity of a part in the currently selected parts.
  function handleQuantityChange(partId: number | null, quantity: string) {
    const currentPartsDeepCopy = deepCopy(currentParts);
    const partIndex = getPartIndex(partId, currentPartsDeepCopy);
    if (partIndex !== -1) {
      if (validateQuantity(quantity)) {
        currentPartsDeepCopy[partIndex].orderQuantity = quantity;
      }
    }
    setCurrentParts(currentPartsDeepCopy);
  }

  // Updates the color of a part in the currently selected parts.
  function handlePaintColorChange(partId: number | null, paintColorId: number | null) {
    const currentPartsDeepCopy = deepCopy(currentParts);
    const partIndex = getPartIndex(partId, currentPartsDeepCopy);
    if (partIndex !== -1) {
      currentPartsDeepCopy[partIndex].paintColorId = paintColorId;
    }
    setCurrentParts(currentPartsDeepCopy);
  }

  // Updates the impeller trim for a part in the currently selected parts.
  function handleImpellerTrimChange(partId: number | null, impellerTrim: string | null) {
    const currentPartsDeepCopy = deepCopy(currentParts);
    const partIndex = getPartIndex(partId, currentPartsDeepCopy);
    if (partIndex !== -1) {
      if (impellerTrim !== null) {
        if (impellerTrim === "" || stringIsValidNumber(impellerTrim)) {
          currentPartsDeepCopy[partIndex].impellerTrim = impellerTrim;
        }
      } else {
        currentPartsDeepCopy[partIndex].impellerTrim = null;
      }
    }
    setCurrentParts(currentPartsDeepCopy);
  }

  // Updates the impeller angle for a part in the currently selected parts.
  function handleImpellerAngleChange(partId: number | null, impellerAngle: string | null) {
    const currentPartsDeepCopy = deepCopy(currentParts);
    const partIndex = getPartIndex(partId, currentPartsDeepCopy);
    if (partIndex !== -1) {
      if (impellerAngle !== null) {
        if (impellerAngle === "" || stringIsValidNumber(impellerAngle)) {
          currentPartsDeepCopy[partIndex].impellerAngle = impellerAngle;
        }
      } else {
        currentPartsDeepCopy[partIndex].impellerAngle = null;
      }
    }
    setCurrentParts(currentPartsDeepCopy);
  }

  // Attempts to select a part from clicking on an SVG.
  function selectPartIdFromSvg(selectedPartNumber: string | null, flatParts: Part[]): void {
    if (selectedPartNumber !== null) {
      for (const flatPart of flatParts) {
        if (flatPart.partNumber.includes(selectedPartNumber)) {
          setSelectedPartId(flatPart.partId);
          break;
        }
      }
    }
  }

  return failedToLoad ? (
    <PartNotSellablePage />
  ) : (
    <Fragment>
      <Spinner loading={loading} />
      {!loading && (
        <div>
          <BomDetails
            bomNumber={bomNumber}
            pumpModel={pumpModel}
            trim={trim}
            serial={serialNumber}
            manualUrl={manualUrl}
            pumpCurves={pumpCurves}
            parts={flatParts}
            onError={(error) => setErrorMessage(error)}
          />
          <div className="container-fluid">
            <div className="row">
              {(svgFound || forceNestedBom) && (
                <div className="col-12 col-xl-5">
                  <BomSvgExplorer
                    bomPath={bomPath}
                    bomParts={bom !== null ? bom.parts : []}
                    bomDescription={bom !== null ? bom.description : ""}
                    bomWeight={bom !== null ? bom.weight : 0.0}
                    hoveredPartNumber={hoveredPartNumber}
                    svgFound={svgFound}
                    onHover={(partNumber) => setHoveredPartNumber(partNumber)}
                    onClick={(partNumber) => selectPartIdFromSvg(partNumber, flatParts)}
                    onNavigate={(bomPath) => setBomPath(bomPath)}
                    onError={() => console.error("Error loading SVG.")}
                  />
                </div>
              )}
              <div className={!svgFound ? "col" : "col-12 col-xl-7"}>
                <div className={!svgFound ? styles.flatPartsList : ""}>
                  <PartsList
                    loading={loading}
                    failedToLoad={failedToLoad}
                    errorMessage={errorMessage}
                    bomIsFlat={bomIsFlat}
                    bomPath={bomPath}
                    hoveredPartNumber={hoveredPartNumber}
                    defaultPaintColorId={defaultPaintColorId}
                    currentParts={currentParts}
                    companyDiscount={companyDiscount}
                    onHover={(partNumber) => setHoveredPartNumber(partNumber)}
                    onNavigate={(bomPath) => setBomPath(bomPath)}
                    onQuantityChange={(partId, orderQuantity) => handleQuantityChange(partId, orderQuantity)}
                    onCustomizePart={(partId) => setSelectedPartId(partId)}
                    onAddToCartError={(message) => setErrorMessage(message)}
                    onAddToCart={(message) => setAddToCartMessage(message)}
                    onAddAllCurrentPartsToCart={() => setShowAddAllPartsToCartModal(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <PartModal
        showModal={selectedPartId !== null || showAddAllPartsToCartModal}
        partId={selectedPartId || null}
        partNumber={selectedPart !== null ? selectedPart.partNumber : ""}
        description={selectedPart !== null ? selectedPart.description : ""}
        buildQuantity={selectedPart !== null ? selectedPart.buildQuantity : ""}
        orderQuantity={selectedPart !== null ? selectedPart.orderQuantity : ""}
        impellerAngle={selectedPart !== null ? selectedPart.impellerAngle : ""}
        impellerTrim={selectedPart !== null ? selectedPart.impellerTrim : ""}
        unitName={selectedPart !== null ? selectedPart.units : ""}
        price={selectedPart !== null ? selectedPart.price : 0}
        companyDiscount={companyDiscount}
        weight={selectedPart !== null ? selectedPart.weight : null}
        isImpeller={selectedPart !== null ? selectedPart.isImpeller : false}
        isSellable={selectedPart !== null ? selectedPart.isSellable : false}
        isPaintable={selectedPart !== null ? selectedPart.isPaintable : false}
        isAddAllOrderableParts={showAddAllPartsToCartModal}
        paintColorId={selectedPart !== null ? selectedPart.paintColorId : null}
        defaultPaintColorId={defaultPaintColorId}
        parts={currentParts}
        onQuantityChange={(selectedPartId, orderQuantity) => handleQuantityChange(selectedPartId, orderQuantity)}
        onPaintColorChange={(selectedPartId, paintColorId) => handlePaintColorChange(selectedPartId, paintColorId)}
        onImpellerTrimChange={(selectedPartId, impellerTrim) => handleImpellerTrimChange(selectedPartId, impellerTrim)}
        onImpellerAngleChange={(selectedPartId, impellerAngle) =>
          handleImpellerAngleChange(selectedPartId, impellerAngle)
        }
        onClose={() => handleCloseModal()}
      />
      <Toast
        title="Part Added"
        message={addToCartMessage}
        show={addToCartMessage.length > 0}
        onClose={() => setAddToCartMessage("")}
        type="success"
        verticalPosition="middle"
        data-test="part-list-item-add-to-cart-toast"
      />
      <Toast
        title="Error"
        message={errorMessage}
        show={errorMessage.length > 0}
        onClose={() => setErrorMessage("")}
        type="error"
        data-test="part-modal-error-toast"
      />
    </Fragment>
  );
}

interface ResponseBody {
  bomNumber: string;
  pumpModel: string;
  trim: number | null;
  serialNumber: string | null;
  manualUrl: string;
  pumpCurves: PumpCurve[];
  bom: PartFromApi;
  companyDiscount: number | null;
  forceNestedBom: boolean;
}

interface CompanyPreferenceResponseBody {
  defaultPaintColor: PaintColor | null;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface PumpCurve {
  name: string;
  url: string;
}

interface PartFromApi {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  hasOtherMaterial: boolean;
  parts: PartFromApi[];
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  paintColorId: number | null;
  orderQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  hasOtherMaterial: boolean;
  parts: PartFromApi[];
}
